import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import schedulingService, {
  type AppointmentSummary,
  type ListAppointmentsFilters,
} from '../../services/scheduling.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';
import ReassignAppointmentModal from './ReassignAppointmentModal';
import CancelAppointmentModal from './CancelAppointmentModal';
import { readerLocale, t } from '../../i18n/language';

interface Props {
  /** Filters applied on every fetch — typically role-driven (agent's
   *  default = `assigned_to=me`; manager's default = no filter). */
  defaultFilters: ListAppointmentsFilters;
  /** What capabilities to surface on each row. Server enforces RBAC
   *  separately; this is presentational. */
  viewerRole: 'agent' | 'manager';
}

interface FilterPreset {
  id: string;
  label: string;
  filters: Partial<ListAppointmentsFilters>;
}

function statusBadge(status: string, isOrphaned: boolean): { bg: string; fg: string; label: string } {
  if (isOrphaned) return { bg: colors.warningBg, fg: colors.warning, label: 'orphaned' };
  if (status === 'scheduled') return { bg: '#EEF2FF', fg: '#4338CA', label: 'scheduled' };
  if (status === 'completed') return { bg: colors.successBg, fg: colors.success, label: 'completed' };
  if (status === 'cancelled') return { bg: colors.dangerBg, fg: colors.danger, label: 'cancelled' };
  if (status === 'no_show') return { bg: colors.dangerBg, fg: colors.danger, label: 'no show' };
  if (status === 'missed') return { bg: colors.warningBg, fg: colors.warning, label: 'missed' };
  return { bg: '#EEF2F7', fg: '#475569', label: status };
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(readerLocale(), {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Shared appointments queue — both agent and manager pages render this
 * with their own `defaultFilters`. Owns:
 *   - data fetching with active preset
 *   - filter chips (My calls / Orphans / All)
 *   - row actions (Claim / Reassign / Cancel)
 *   - modal state for Reassign / Cancel flows
 */
export default function AppointmentList({
  defaultFilters,
  viewerRole,
}: Readonly<Props>): JSX.Element {
  // Filter presets — same chips for both roles, but the default-active
  // one differs based on which page wraps us.
  const presets: FilterPreset[] = [
    { id: 'mine',      label: 'My calls',  filters: { assigned_to: 'me' } },
    // Team-wide ACTIVE queue (scheduled only) — the manager's default. Keeps
    // the default view focused on upcoming/actionable calls, not history.
    { id: 'scheduled', label: 'Scheduled', filters: { status: ['scheduled'] } },
    { id: 'orphans',   label: 'Orphans',   filters: { assigned_to: 'orphaned' } },
    // 'All' must include TERMINAL states — the backend defaults to
    // scheduled-only when `status` is omitted, so pass them explicitly.
    // Otherwise completed/cancelled/no_show/missed calls are invisible and
    // the disposition/expiry outcomes can never be reviewed.
    { id: 'all',       label: 'All',
      filters: { status: ['scheduled', 'completed', 'cancelled', 'no_show', 'missed'] } },
  ];
  // Agent default = their own calls; manager (no assigned_to) default = the
  // team's active scheduled queue (NOT 'all' — that would flood the default
  // view with every past/cancelled/missed call).
  let initialPresetId = 'scheduled';
  if (defaultFilters.assigned_to === 'me') {
    initialPresetId = 'mine';
  } else if (
    defaultFilters.assigned_to === 'orphaned' ||
    defaultFilters.assigned_to === 'orphan'
  ) {
    initialPresetId = 'orphans';
  }

  const [activePreset, setActivePreset] = useState<string>(initialPresetId);
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState<{ items: AppointmentSummary[]; total: number; size: number } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFlash, setActionFlash] = useState<string | null>(null);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  const [reassignTarget, setReassignTarget] = useState<AppointmentSummary | null>(null);
  const [cancelTarget, setCancelTarget] = useState<AppointmentSummary | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const preset = presets.find((p) => p.id === activePreset);
      const merged: ListAppointmentsFilters = {
        ...defaultFilters,
        ...preset?.filters,
        page,
        size: 25,
      };
      const res = await schedulingService.listAppointments(merged);
      setData({ items: res.items, total: res.total, size: res.size });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not load appointments.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePreset, page, defaultFilters]);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleClaim(appt: AppointmentSummary): Promise<void> {
    if (!globalThis.confirm(t('Claim this appointment? It will move to your queue.'))) return;
    setActionInFlight(appt.id);
    try {
      await schedulingService.claimAppointment(appt.id);
      setActionFlash(`Claimed ${fullName(appt.member)}'s appointment.`);
      await refresh();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not claim this appointment.'));
    } finally {
      setActionInFlight(null);
    }
  }

  async function handleDisposition(
    appt: AppointmentSummary, outcome: 'completed' | 'no_show',
  ): Promise<void> {
    setActionInFlight(appt.id);
    try {
      await schedulingService.dispositionAppointment(appt.id, outcome);
      setActionFlash(
        outcome === 'completed'
          ? `Marked ${fullName(appt.member)}'s call completed.`
          : `Marked ${fullName(appt.member)} a no-show.`,
      );
      await refresh();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not record the outcome.'));
    } finally {
      setActionInFlight(null);
    }
  }

  function handleReassigned(_updated: AppointmentSummary): void {
    setReassignTarget(null);
    setActionFlash('Appointment reassigned.');
    refresh();
  }

  function handleCancelled(_updated: AppointmentSummary): void {
    setCancelTarget(null);
    setActionFlash('Appointment cancelled.');
    refresh();
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;

  return (
    <>
      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {presets.map((p) => {
          const isActive = activePreset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => { setActivePreset(p.id); setPage(1); }}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
              style={{
                background: isActive ? colors.navy : colors.surface,
                color: isActive ? '#FFFFFF' : colors.inkSoft,
                border: `1px solid ${isActive ? colors.navy : colors.border}`,
              }}
            >
              {p.label}
            </button>
          );
        })}
        <span className="text-xs ml-auto" style={{ color: colors.mutedSoft }}>
          {data ? `${data.total} total · page ${page} of ${totalPages}` : ''}
        </span>
      </div>

      {actionFlash && (
        <output className="rounded-lg p-3 mb-4 text-sm flex items-start justify-between gap-3"
             style={{ background: colors.successBg, color: colors.success, border: `1px solid #A7F3D0` }}>
          <span>{actionFlash}</span>
          <button type="button" onClick={() => setActionFlash(null)}
                  className="text-xs underline flex-shrink-0" style={{ color: colors.success }}>
            Dismiss
          </button>
        </output>
      )}

      {error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      {loading && !data && (
        <p className="text-sm" style={{ color: colors.muted }}>Loading appointments…</p>
      )}

      {!loading && data?.items.length === 0 && (
        <div className="rounded-2xl p-6 text-sm"
             style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
          No appointments match these filters.
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="overflow-hidden rounded-2xl"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
          <table className="w-full text-sm">
            <thead style={{ background: colors.canvas, color: colors.muted }}>
              <tr>
                <th className="text-left font-medium px-4 py-3">When</th>
                <th className="text-left font-medium px-4 py-3">Member</th>
                <th className="text-left font-medium px-4 py-3">Agent</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((appt, idx) => {
                const badge = statusBadge(appt.status, appt.is_orphaned);
                const showClaim = viewerRole === 'agent' && appt.is_orphaned && appt.status === 'scheduled';
                const showReassign = viewerRole === 'manager' && appt.status === 'scheduled';
                const showCancel = appt.status === 'scheduled';
                // Disposition (log the call outcome) once the slot has ended:
                // the owning agent — or any manager — records completed/no-show.
                const hasEnded = new Date(appt.ends_at).getTime() < Date.now();
                const showDisposition = appt.status === 'scheduled' && hasEnded
                  && (viewerRole === 'manager' || !appt.is_orphaned);
                // A call the agent holds opens the call screen for its client. A call claimed
                // to cover an absent colleague opens it too, while the absence lasts.
                const showCallGuide = viewerRole === 'agent' && appt.status === 'scheduled'
                  && !appt.is_orphaned && appt.needs_my_action;
                const isWorking = actionInFlight === appt.id;

                return (
                  <tr key={appt.id}
                      style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium" style={{ color: colors.ink }}>
                        {formatDateTime(appt.starts_at)}
                      </div>
                      {appt.purpose && (
                        <div className="text-xs mt-0.5" style={{ color: colors.mutedSoft }}>
                          {appt.purpose}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium" style={{ color: colors.ink }}>
                        {fullName(appt.member)}
                      </div>
                      <div className="text-xs" style={{ color: colors.mutedSoft }}>
                        {appt.member.email}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div style={{ color: colors.inkSoft }}>
                        {appt.agent ? fullName(appt.agent) : (
                          <span style={{ color: colors.warning }}>Unassigned</span>
                        )}
                      </div>
                      {appt.is_orphaned && appt.original_agent.id !== appt.agent?.id && (
                        <div className="text-xs" style={{ color: colors.mutedSoft }}>
                          was: {fullName(appt.original_agent)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                            style={{ background: badge.bg, color: badge.fg }}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="inline-flex gap-2 flex-wrap justify-end">
                        {showClaim && (
                          <button type="button"
                                  onClick={() => handleClaim(appt)}
                                  disabled={isWorking}
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                                  style={{
                                    background: colors.navy, color: '#FFFFFF',
                                    opacity: isWorking ? 0.6 : 1,
                                    cursor: isWorking ? 'wait' : 'pointer',
                                  }}>
                            {isWorking ? '…' : 'Claim'}
                          </button>
                        )}
                        {showCallGuide && (
                          <Link to={`/agent/call-guide/${appt.member.id}`}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                style={{
                                  background: colors.surface, color: colors.inkSoft,
                                  border: `1px solid ${colors.border}`,
                                }}>
                            Call guide
                          </Link>
                        )}
                        {showReassign && (
                          <button type="button"
                                  onClick={() => setReassignTarget(appt)}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                  style={{
                                    background: colors.surface, color: colors.inkSoft,
                                    border: `1px solid ${colors.border}`,
                                  }}>
                            Reassign
                          </button>
                        )}
                        {showDisposition && (
                          <>
                            <button type="button"
                                    onClick={() => handleDisposition(appt, 'completed')}
                                    disabled={isWorking}
                                    title="The call happened"
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                                    style={{
                                      background: colors.successBg, color: colors.success,
                                      border: `1px solid ${colors.successBorder}`,
                                      opacity: isWorking ? 0.6 : 1,
                                      cursor: isWorking ? 'wait' : 'pointer',
                                    }}>
                              {isWorking ? '…' : 'Completed'}
                            </button>
                            <button type="button"
                                    onClick={() => handleDisposition(appt, 'no_show')}
                                    disabled={isWorking}
                                    title="The client did not show"
                                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                    style={{
                                      background: colors.surface, color: colors.inkSoft,
                                      border: `1px solid ${colors.border}`,
                                      opacity: isWorking ? 0.6 : 1,
                                    }}>
                              No-show
                            </button>
                          </>
                        )}
                        {showCancel && (
                          <button type="button"
                                  onClick={() => setCancelTarget(appt)}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                  style={{
                                    background: colors.surface, color: colors.danger,
                                    border: `1px solid ${colors.dangerBorder}`,
                                  }}>
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-4">
          <button type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}`,
                    opacity: page <= 1 ? 0.5 : 1,
                  }}>
            ← Previous
          </button>
          <button type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}`,
                    opacity: page >= totalPages ? 0.5 : 1,
                  }}>
            Next →
          </button>
        </div>
      )}

      {reassignTarget && (
        <ReassignAppointmentModal
          appointment={reassignTarget}
          onClose={() => setReassignTarget(null)}
          onReassigned={handleReassigned}
        />
      )}
      {cancelTarget && (
        <CancelAppointmentModal
          appointment={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={handleCancelled}
        />
      )}
    </>
  );
}
